import { Controller } from "@hotwired/stimulus";

// 帳票出力画面用コントローラー。
// inspections_search_form_controller.ts（点検一覧画面用）とカスケード・検索ロジックはほぼ同一だが、
// 期間の選択方式が異なる（こちらは月単位チェックボックス、あちらは日付範囲）ため、
// 将来の画面固有の拡張性を考慮してあえてファイルを分けている。

// カスケードと検索の可視性を分離管理するためのdata属性キー
const CASCADE_HIDDEN = "cascadeHidden";
const CASCADE_PRE_CHECKED = "cascadePreChecked";
const SEARCH_HIDDEN = "searchHidden";
const SEARCH_PRE_CHECKED = "searchPreChecked";

export default class extends Controller {
  static targets = [
    "monthItem",
    "siteItem",
    "nullSiteCheckbox",
    "siteCheckbox",
    "checklistCheckbox",
    "categoryGroup",
    "resourceGroup",
    "noMonthsMessage",
    "noSitesMessage",
    "noCategoriesMessage",
    "noResourcesMessage",
    "submitButton",
  ];

  declare readonly monthItemTargets: HTMLElement[];
  declare readonly siteItemTargets: HTMLElement[];
  declare readonly nullSiteCheckboxTargets: HTMLInputElement[];
  declare readonly siteCheckboxTargets: HTMLInputElement[];
  declare readonly checklistCheckboxTargets: HTMLInputElement[];
  declare readonly categoryGroupTargets: HTMLElement[];
  declare readonly resourceGroupTargets: HTMLElement[];
  declare readonly noMonthsMessageTargets: HTMLElement[];
  declare readonly noSitesMessageTargets: HTMLElement[];
  declare readonly noCategoriesMessageTargets: HTMLElement[];
  declare readonly noResourcesMessageTargets: HTMLElement[];
  declare readonly submitButtonTargets: HTMLButtonElement[];

  connect(): void {
    this.applyCascadeFilters();
  }

  // --- テキスト検索フィルター ---

  filterByMonth(event: Event): void {
    const text = this.getInputValue(event);
    this.applySearchFlatList(this.monthItemTargets, text);
    this.updateEmptyMessage(this.noMonthsMessageTargets, this.monthItemTargets);
    this.applyCascadeFilters();
  }

  filterBySite(event: Event): void {
    const text = this.getInputValue(event);
    this.applySearchFlatList(this.siteItemTargets, text);
    this.updateEmptyMessage(this.noSitesMessageTargets, this.siteItemTargets);
    this.applyCascadeForCategories();
    this.applyCascadeForResources();
  }

  filterByCategory(event: Event): void {
    const text = this.getInputValue(event);
    this.applySearchHierarchicalList(this.categoryGroupTargets, text);
    this.updateEmptyMessage(this.noCategoriesMessageTargets, this.categoryGroupTargets);
    this.applyCascadeForResources();
  }

  filterByResource(event: Event): void {
    const text = this.getInputValue(event);
    this.applySearchHierarchicalList(this.resourceGroupTargets, text);
    this.updateEmptyMessage(this.noResourcesMessageTargets, this.resourceGroupTargets);
  }

  // --- カスケード連動 ---

  onMonthChange(): void {
    this.applyCascadeFilters();
  }

  onSiteChange(): void {
    this.applyCascadeForCategories();
    this.applyCascadeForResources();
  }

  onChecklistChange(): void {
    this.applyCascadeForResources();
  }

  onResourceChange(): void {
    this.updateSubmitButton();
  }

  // --- 親子チェックボックス連動 ---

  toggleGroupChildren(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const group = checkbox.closest<HTMLElement>("[data-child-role]")!;
    group.querySelectorAll<HTMLElement>("[data-role]").forEach((item) => {
      if (this.isHidden(item)) return;
      const cb = item.querySelector<HTMLInputElement>("input[type='checkbox']");
      if (cb) cb.checked = checkbox.checked;
    });
  }

  updateGroupParent(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    let currentItem = checkbox.closest<HTMLElement>("[data-role]");
    while (currentItem) {
      const group = currentItem.parentElement?.closest<HTMLElement>("[data-child-role]");
      if (!group) break;
      const childRole = group.dataset.childRole!;
      const visibleCheckboxes = Array.from(group.querySelectorAll<HTMLElement>(`[data-role='${childRole}']`))
        .filter((el) => !this.isHidden(el))
        .map((el) => el.querySelector<HTMLInputElement>("input[type='checkbox']"))
        .filter((cb): cb is HTMLInputElement => cb !== null);
      const allChecked = visibleCheckboxes.length > 0 && visibleCheckboxes.every((cb) => cb.checked);
      const parentCheckbox = group.querySelector<HTMLInputElement>(":scope > label input[type='checkbox']");
      if (parentCheckbox) parentCheckbox.checked = allChecked;
      currentItem = group;
    }
  }

  // --- カスケードフィルター実装 ---

  private applyCascadeFilters(): void {
    this.applyCascadeForSites();
    this.applyCascadeForCategories();
    this.applyCascadeForResources();
  }

  private applyCascadeForSites(): void {
    const selectedMonths = this.getSelectedMonths();
    this.siteItemTargets.forEach((item) => {
      const months = (item.dataset.inspectionMonths || "").split(",").filter(Boolean);
      const visible = months.some((m) => selectedMonths.includes(m));
      this.setCascadeHidden(item, !visible);
    });
    this.updateEmptyMessage(this.noSitesMessageTargets, this.siteItemTargets);
  }

  private applyCascadeForCategories(): void {
    const selectedMonths = this.getSelectedMonths();
    const selectedSiteContext = this.getSelectedSiteContext();

    this.categoryGroupTargets.forEach((group) => {
      let groupHasVisible = false;

      group.querySelectorAll<HTMLElement>("[data-check-list-id]").forEach((item) => {
        const siteMonths = (item.dataset.inspectionSiteMonths || "").split(",").filter(Boolean);
        const visible = siteMonths.some((pair) => {
          const colonIdx = pair.indexOf(":");
          const siteId = pair.substring(0, colonIdx);
          const month = pair.substring(colonIdx + 1);
          if (!selectedMonths.includes(month)) return false;
          return this.siteContextMatches(siteId, selectedSiteContext);
        });
        this.setCascadeHidden(item, !visible);
        if (visible) groupHasVisible = true;
      });

      // サブグループの表示: 配下に表示中のチェックリストがあるか
      group.querySelectorAll<HTMLElement>("[data-role='category-sub-group']").forEach((subGroup) => {
        const hasVisibleChecklist = Array.from(subGroup.querySelectorAll<HTMLElement>("[data-check-list-id]")).some(
          (cl) => !this.isHidden(cl),
        );
        this.setCascadeHidden(subGroup, !hasVisibleChecklist);
      });

      this.setCascadeHidden(group, !groupHasVisible);
    });
    this.updateEmptyMessage(this.noCategoriesMessageTargets, this.categoryGroupTargets);
  }

  private applyCascadeForResources(): void {
    const selectedMonths = this.getSelectedMonths();
    const selectedSiteContext = this.getSelectedSiteContext();
    const selectedChecklistIds = this.getSelectedChecklistIds();

    this.resourceGroupTargets.forEach((group) => {
      let groupHasVisible = false;

      group.querySelectorAll<HTMLElement>("[data-role='resource-item']").forEach((item) => {
        const pairs = (item.dataset.inspectionPairs || "").split(",").filter(Boolean);
        const visible = pairs.some((triple) => {
          const firstColon = triple.indexOf(":");
          const secondColon = triple.indexOf(":", firstColon + 1);
          const clId = triple.substring(0, firstColon);
          const siteId = triple.substring(firstColon + 1, secondColon);
          const month = triple.substring(secondColon + 1);
          if (!selectedMonths.includes(month)) return false;
          if (!this.siteContextMatches(siteId, selectedSiteContext)) return false;
          return selectedChecklistIds.includes(clId);
        });
        this.setCascadeHidden(item, !visible);
        if (visible) groupHasVisible = true;
      });

      this.setCascadeHidden(group, !groupHasVisible);
      this.updateGroupParentCheckboxes(group);
    });
    this.updateEmptyMessage(this.noResourcesMessageTargets, this.resourceGroupTargets);
    this.updateSubmitButton();
  }

  // --- ヘルパー ---

  private getSelectedMonths(): string[] {
    return this.monthItemTargets
      .filter((item) => {
        if (item.dataset[SEARCH_HIDDEN]) return false;
        const cb = item.querySelector<HTMLInputElement>("input[type='checkbox']");
        return cb?.checked;
      })
      .map((item) => item.dataset.month || "");
  }

  private getSelectedSiteContext(): { siteIds: string[]; includeNull: boolean } {
    const siteIds = this.siteCheckboxTargets.filter((cb) => cb.checked && !this.isSearchHidden(cb)).map((cb) => cb.value);
    const includeNull = this.nullSiteCheckboxTargets.some((cb) => cb.checked && !this.isSearchHidden(cb));
    return { siteIds, includeNull };
  }

  private getSelectedChecklistIds(): string[] {
    return this.checklistCheckboxTargets.filter((cb) => cb.checked && !this.isSearchHidden(cb)).map((cb) => cb.value);
  }

  private siteContextMatches(siteId: string, context: { siteIds: string[]; includeNull: boolean }): boolean {
    if (siteId === "") return context.includeNull;
    return context.siteIds.includes(siteId);
  }

  // --- 可視性の状態分離管理 ---

  private setCascadeHidden(item: HTMLElement, hidden: boolean): void {
    if (hidden) {
      if (!item.dataset[CASCADE_HIDDEN]) {
        // 新たにカスケード非表示になる: チェック状態を保存してOFFにする
        const cb = item.querySelector<HTMLInputElement>("input[type='checkbox']");
        if (cb) {
          item.dataset[CASCADE_PRE_CHECKED] = cb.checked ? "true" : "false";
          cb.checked = false;
        }
      }
      item.dataset[CASCADE_HIDDEN] = "true";
    } else {
      if (item.dataset[CASCADE_HIDDEN]) {
        // カスケード非表示から復帰: 保存したチェック状態を復元する
        const preChecked = item.dataset[CASCADE_PRE_CHECKED];
        if (preChecked !== undefined) {
          const cb = item.querySelector<HTMLInputElement>("input[type='checkbox']");
          if (cb) cb.checked = preChecked === "true";
          delete item.dataset[CASCADE_PRE_CHECKED];
        }
      }
      delete item.dataset[CASCADE_HIDDEN];
    }
    this.applyDisplay(item);
  }

  private setSearchHidden(item: HTMLElement, hidden: boolean): void {
    if (hidden) {
      if (!item.dataset[SEARCH_HIDDEN]) {
        // 新たに検索非表示になる: チェック状態を保存してOFFにする
        const cb = item.querySelector<HTMLInputElement>("input[type='checkbox']");
        if (cb) {
          item.dataset[SEARCH_PRE_CHECKED] = cb.checked ? "true" : "false";
          cb.checked = false;
        }
      }
      item.dataset[SEARCH_HIDDEN] = "true";
    } else {
      if (item.dataset[SEARCH_HIDDEN]) {
        // 検索非表示から復帰: 保存したチェック状態を復元する
        const preChecked = item.dataset[SEARCH_PRE_CHECKED];
        if (preChecked !== undefined) {
          const cb = item.querySelector<HTMLInputElement>("input[type='checkbox']");
          if (cb) cb.checked = preChecked === "true";
          delete item.dataset[SEARCH_PRE_CHECKED];
        }
      }
      delete item.dataset[SEARCH_HIDDEN];
    }
    this.applyDisplay(item);
  }

  private applyDisplay(item: HTMLElement): void {
    item.style.display = item.dataset[CASCADE_HIDDEN] || item.dataset[SEARCH_HIDDEN] ? "none" : "";
  }

  private isHidden(el: HTMLElement): boolean {
    return el.style.display === "none";
  }

  private isSearchHidden(el: HTMLElement): boolean {
    const item = el.closest<HTMLElement>("[data-search-text]") || el.closest<HTMLElement>("[data-site-id]");
    return item?.dataset[SEARCH_HIDDEN] === "true";
  }

  private updateGroupParentCheckboxes(group: HTMLElement): void {
    // 深い階層から浅い階層へ更新するため、depth順でソート
    const elements = Array.from(group.querySelectorAll<HTMLElement>("[data-child-role]"));
    elements
      .sort((a, b) => this.getDepth(b, group) - this.getDepth(a, group))
      .forEach((g) => {
        const childRole = g.dataset.childRole!;
        const visibleChildren = Array.from(g.querySelectorAll<HTMLElement>(`[data-role='${childRole}']`))
          .filter((el) => !this.isHidden(el))
          .map((el) => el.querySelector<HTMLInputElement>("input[type='checkbox']"))
          .filter((cb): cb is HTMLInputElement => cb !== null);
        const allChecked = visibleChildren.length > 0 && visibleChildren.every((cb) => cb.checked);
        const parentCb = g.querySelector<HTMLInputElement>(":scope > label input[type='checkbox']");
        if (parentCb) parentCb.checked = allChecked;
      });
  }

  private getDepth(element: HTMLElement, root: HTMLElement): number {
    let depth = 0;
    let current = element.parentElement;
    while (current && current !== root) {
      depth += 1;
      current = current.parentElement;
    }
    return depth;
  }

  // --- テキスト検索 ---

  private applySearchFlatList(items: HTMLElement[], text: string): void {
    items.forEach((item) => {
      this.setSearchHidden(item, !this.matchesSearchText(item, text));
    });
  }

  private applySearchHierarchicalList(groups: HTMLElement[], text: string): void {
    groups.forEach((group) => {
      if (!text) {
        // 検索クリア: 検索非表示を全て解除（カスケード状態は維持）
        group.querySelectorAll<HTMLElement>("[data-search-text]").forEach((el) => {
          this.setSearchHidden(el, false);
        });
        this.setSearchHidden(group, false);
        this.updateGroupParentCheckboxes(group);
        return;
      }

      const groupMatches = this.matchesSearchText(group, text);
      if (groupMatches) {
        group.querySelectorAll<HTMLElement>("[data-search-text]").forEach((el) => {
          this.setSearchHidden(el, false);
        });
        this.setSearchHidden(group, false);
        this.updateGroupParentCheckboxes(group);
        return;
      }

      const allItems = Array.from(group.querySelectorAll<HTMLElement>("[data-search-text]"));
      const visibleSet = new Set<HTMLElement>();
      allItems.forEach((el) => {
        if (!this.matchesSearchText(el, text)) return;
        visibleSet.add(el);
        el.querySelectorAll<HTMLElement>("[data-search-text]").forEach((desc) => visibleSet.add(desc));
        let ancestor = el.parentElement?.closest<HTMLElement>("[data-search-text]");
        while (ancestor && group.contains(ancestor)) {
          visibleSet.add(ancestor);
          ancestor = ancestor.parentElement?.closest<HTMLElement>("[data-search-text]") || null;
        }
      });

      allItems.forEach((el) => {
        this.setSearchHidden(el, !visibleSet.has(el));
      });
      this.setSearchHidden(group, visibleSet.size === 0);
      this.updateGroupParentCheckboxes(group);
    });
  }

  private updateEmptyMessage(messageTargets: HTMLElement[], items: HTMLElement[]): void {
    const anyVisible = items.some((item) => !this.isHidden(item));
    messageTargets.forEach((msg) => msg.classList.toggle("hidden", anyVisible));
  }

  private matchesSearchText(el: HTMLElement, text: string): boolean {
    const searchText = el.dataset.searchText || "";
    return searchText.toLowerCase().includes(text);
  }

  private getInputValue(event: Event): string {
    return ((event.target as HTMLInputElement).value || "").toLowerCase();
  }

  private updateSubmitButton(): void {
    const hasVisibleResource = this.resourceGroupTargets.some((group) => {
      if (this.isHidden(group)) return false;
      return Array.from(group.querySelectorAll<HTMLElement>("[data-role='resource-item']")).some(
        (item) => !this.isHidden(item) && item.querySelector<HTMLInputElement>("input[type='checkbox']")?.checked,
      );
    });

    this.submitButtonTargets.forEach((button) => {
      button.disabled = !hasVisibleResource;
      button.classList.toggle("opacity-50", !hasVisibleResource);
      button.classList.toggle("cursor-not-allowed", !hasVisibleResource);
      button.classList.toggle("cursor-pointer", hasVisibleResource);
    });
  }
}
